import { HcContact } from "./hc-contact.model";
import { HcMessageStrings, HcVoiceInstructions } from "./strings/hc-message-strings";

export interface HcNotificationSetup {
  _id: string;
  csId: string;
  flowId: string; // Reference to HcFlow._id
  contacts: HcNotificationContact[];
  // Determines how the first notification is initiated for each response meaning
  firstMethodForNotOk: HcCommunicationMethodType; // Initial method when meaning is NOT_OK
  firstMethodForNeedHelp: HcCommunicationMethodType; // Initial method when meaning is NEED_HELP
  /**
   * Intended cap for contact-notification delivery / escalation (e.g. NOT_OK reminders).
   * Does **not** control how many times the loved-one health check (SMS/voice) is re-sent;
   * that is always `HcFlow.schedule.retries`.
   */
  maxRetries: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface HcNotificationContact {
  contactId: string; // Reference to HcContact._id
  contact?: HcContact; // Populated on queries
  priority: number; // Lower number = higher priority
  isActive: boolean;
}

export enum HcCommunicationMethodType {
  SMS = 'HcCommunicationMethodType.sms',
  VOICE_CALL = 'HcCommunicationMethodType.voice_call'
}

export interface HcEmergencySmsInstructions {
  message: string;
  languageId: string;
}

export interface HcNotificationVoiceInstructions {
  emergency: HcVoiceInstructions;
  notOk: HcVoiceInstructions;
  noAnswer: HcVoiceInstructions;
}

export interface HcNotificationSmsInstructions {
  emergency: HcEmergencySmsInstructions;
  notOk: HcEmergencySmsInstructions;
  noAnswer: HcEmergencySmsInstructions;
}

/**
 * Generates emergency voice call instructions in the specified language
 * @param languageId - The language id (e.g., 'en', 'fr', 'es')
 * @param lovedOneName - The name of the loved one to include in the message
 * @returns HcVoiceInstructions object with greeting, message, prompt and timeout messages
 */
export function generateEmergencyVoiceInstructions(languageId: string, lovedOneName?: string): HcVoiceInstructions {
  // All emergency voice messages have been migrated to HcMessageStrings.HcVoice.getEmergency
  return HcMessageStrings.HcVoice.getEmergency(languageId, lovedOneName);
}

/**
 * Generates all notification voice call instructions in the specified language
 * @param languageId - The language id (e.g., 'en', 'fr', 'es')
 * @param lovedOneName - The name of the loved one to include in the message
 * @returns HcNotificationVoiceInstructions object with emergency, notOk, and noAnswer instructions
 */
export function generateNotificationVoiceInstructions(languageId: string, lovedOneName?: string): HcNotificationVoiceInstructions {
  // Emergency instructions (for NEED_HELP)
  const emergency = generateEmergencyVoiceInstructions(languageId, lovedOneName);

  // Not OK instructions (for NOT_OK - less urgent)
  const notOk = generateNotOkVoiceInstructions(languageId, lovedOneName);

  // No answer instructions (for when there's no response to the health check call)
  const noAnswer = generateNoAnswerVoiceInstructions(languageId, lovedOneName);

  return {
    emergency,
    notOk,
    noAnswer
  };
}

/**
 * Generates "not OK" voice call instructions in the specified language
 * @param languageId - The language id (e.g., 'en', 'fr', 'es')
 * @param lovedOneName - The name of the loved one to include in the message
 * @returns HcVoiceInstructions object with greeting, message, prompt and timeout messages
 */
export function generateNotOkVoiceInstructions(languageId: string, lovedOneName?: string): HcVoiceInstructions {
  // All NotOk voice messages have been migrated to HcMessageStrings.HcVoice.getNotOk
  return HcMessageStrings.HcVoice.getNotOk(languageId, lovedOneName);
}

/**
 * Generates "no answer" voice call instructions in the specified language
 * @param languageId - The language id (e.g., 'en', 'fr', 'es')
 * @param lovedOneName - The name of the loved one to include in the message
 * @returns HcVoiceInstructions object with greeting, message, prompt and timeout messages
 */
export function generateNoAnswerVoiceInstructions(languageId: string, lovedOneName?: string): HcVoiceInstructions {
  // All NoAnswer voice messages have been migrated to HcMessageStrings.HcVoice.getNoAnswer
  return HcMessageStrings.HcVoice.getNoAnswer(languageId, lovedOneName);
}

/**
 * Generates emergency SMS instructions in the specified language
 * @param languageId - The language id (e.g., 'en', 'fr', 'es')
 * @param lovedOneName - The name of the loved one to include in the message
 * @returns HcEmergencySmsInstructions object with message
 */
export function generateEmergencySmsInstructions(languageId: string, lovedOneName?: string): HcEmergencySmsInstructions {
  // Extract SMS from voice instructions
  const voiceInstructions = HcMessageStrings.HcVoice.getEmergency(languageId, lovedOneName);

    return {
      message: voiceInstructions.sms,
      languageId: languageId
    };

}

/**
 * Generates "not OK" SMS instructions in the specified language
 * @param languageId - The language id (e.g., 'en', 'fr', 'es')
 * @param lovedOneName - The name of the loved one to include in the message
 * @returns HcEmergencySmsInstructions object with message
 */
export function generateNotOkSmsInstructions(languageId: string, lovedOneName?: string): HcEmergencySmsInstructions {
  // Extract SMS from voice instructions
  const voiceInstructions = HcMessageStrings.HcVoice.getNotOk(languageId, lovedOneName);
    return {
      message: voiceInstructions.sms,
      languageId: languageId
    };

}

/**
 * Generates "no answer" SMS instructions in the specified language
 * @param languageId - The language id (e.g., 'en', 'fr', 'es')
 * @param lovedOneName - The name of the loved one to include in the message
 * @returns HcEmergencySmsInstructions object with message
 */
export function generateNoAnswerSmsInstructions(languageId: string, lovedOneName?: string): HcEmergencySmsInstructions {
  // Extract SMS from voice instructions
  const voiceInstructions = HcMessageStrings.HcVoice.getNoAnswer(languageId, lovedOneName);
    return {
      message: voiceInstructions.sms,
      languageId: languageId
    };

}

/**
 * Generates all notification SMS instructions in the specified language
 * @param languageId - The language id (e.g., 'en', 'fr', 'es')
 * @param lovedOneName - The name of the loved one to include in the message
 * @returns HcNotificationSmsInstructions object with emergency, notOk, and noAnswer instructions
 */
export function generateNotificationSmsInstructions(languageId: string, lovedOneName?: string): HcNotificationSmsInstructions {
  // Emergency instructions (for NEED_HELP)
  const emergency = generateEmergencySmsInstructions(languageId, lovedOneName);

  // Not OK instructions (for NOT_OK - less urgent)
  const notOk = generateNotOkSmsInstructions(languageId, lovedOneName);

  // No answer instructions (for when there's no response to the health check call)
  const noAnswer = generateNoAnswerSmsInstructions(languageId, lovedOneName);

  return {
    emergency,
    notOk,
    noAnswer
  };
}


